import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { adminGetCallAnalytics, adminGetRevenueAnalytics, adminGetCustomers, adminGetAgents } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { PhoneCall, Clock, TrendingUp, DollarSign, CreditCard, Package, Check, ChevronsUpDown } from 'lucide-react';

const COLORS = ['#0066FF', '#0EA5E9', '#10B981', '#F59E0B'];

export function AdminAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [periodType, setPeriodType] = useState('30');
  const [businessFilter, setBusinessFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [businessOpen, setBusinessOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    loadFilterOptions();
    loadData();
  }, []);

  useEffect(() => {
    loadAgentsForSelectedBusiness();
  }, [businessFilter]);

  const loadFilterOptions = async () => {
    try {
      setLoadingFilters(true);
      const [customersRes, agentsRes] = await Promise.all([
        adminGetCustomers(200, 0),
        adminGetAgents({ limit: 200, skip: 0 }),
      ]);
      setCustomers(customersRes?.data?.customers || []);
      setAgents(agentsRes?.data?.agents || []);
    } catch (error) {
      toast.error('Failed to load filter options');
    } finally {
      setLoadingFilters(false);
    }
  };

  const loadAgentsForSelectedBusiness = async () => {
    try {
      const params = { limit: 200, skip: 0 };
      if (businessFilter) params.customer_id = businessFilter;
      const response = await adminGetAgents(params);
      setAgents(response?.data?.agents || []);
      if (agentFilter && !response?.data?.agents?.some((a) => a.agent_id === agentFilter)) {
        setAgentFilter('');
      }
    } catch (error) {
      // keep page functional even if options fail to refresh
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};

      if (periodType === 'last_1_day') {
        params.time_range = 'last_1_day';
      } else if (periodType === 'custom') {
        if (!startDate || !endDate) {
          toast.error('Select both start and end date for custom range');
          setLoading(false);
          return;
        }
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.days = parseInt(periodType, 10);
      }

      if (businessFilter) params.business_id = businessFilter;
      if (agentFilter) params.agent_id = agentFilter;

      const response = await adminGetCallAnalytics(params);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <AnalyticsSkeleton />;
  }

  const chartData = data?.daily_stats?.map(d => ({
    date: d._id,
    calls: d.calls,
    minutes: d.minutes,
  })) || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-jakarta font-bold text-slate-900">Call Analytics</h1>
          <p className="text-slate-500">Track call performance and usage</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setFiltersOpen((prev) => !prev)}
          data-testid="toggle-analytics-filters-btn"
        >
          {filtersOpen ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </div>

      {filtersOpen && (
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Time Range</Label>
                <Select value={periodType} onValueChange={setPeriodType}>
                  <SelectTrigger data-testid="period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Last 1 day</SelectItem>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="last_1_day">Last 24 hours</SelectItem>
                    <SelectItem value="custom">Custom range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {periodType === 'custom' && (
                <>
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label>Business / Customer</Label>
                <Popover open={businessOpen} onOpenChange={setBusinessOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between"
                      disabled={loadingFilters}
                      data-testid="analytics-business-combobox"
                    >
                      {businessFilter
                        ? (customers.find((customer) => customer.user_id === businessFilter)?.company_name
                          || customers.find((customer) => customer.user_id === businessFilter)?.name
                          || customers.find((customer) => customer.user_id === businessFilter)?.email
                          || businessFilter)
                        : 'All Businesses'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[420px] p-0">
                    <Command>
                      <CommandInput placeholder="Search business/customer..." />
                      <CommandList>
                        <CommandEmpty>No business found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all businesses"
                            onSelect={() => {
                              setBusinessFilter('');
                              setBusinessOpen(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${businessFilter ? 'opacity-0' : 'opacity-100'}`} />
                            All Businesses
                          </CommandItem>
                          {customers.map((customer) => {
                            const label = customer.company_name || customer.name || customer.email || customer.user_id;
                            return (
                              <CommandItem
                                key={customer.user_id}
                                value={`${label} ${customer.user_id} ${customer.email || ''}`}
                                onSelect={() => {
                                  setBusinessFilter(customer.user_id);
                                  setBusinessOpen(false);
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${businessFilter === customer.user_id ? 'opacity-100' : 'opacity-0'}`} />
                                {label} ({customer.user_id})
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Agent</Label>
                <Popover open={agentOpen} onOpenChange={setAgentOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between"
                      disabled={loadingFilters}
                      data-testid="analytics-agent-combobox"
                    >
                      {agentFilter
                        ? (agents.find((agent) => agent.agent_id === agentFilter)?.name || agentFilter)
                        : 'All Agents'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[420px] p-0">
                    <Command>
                      <CommandInput placeholder="Search agent..." />
                      <CommandList>
                        <CommandEmpty>No agent found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all agents"
                            onSelect={() => {
                              setAgentFilter('');
                              setAgentOpen(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${agentFilter ? 'opacity-0' : 'opacity-100'}`} />
                            All Agents
                          </CommandItem>
                          {agents.map((agent) => (
                            <CommandItem
                              key={agent.agent_id}
                              value={`${agent.name || 'Unnamed Agent'} ${agent.agent_id}`}
                              onSelect={() => {
                                setAgentFilter(agent.agent_id);
                                setAgentOpen(false);
                              }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${agentFilter === agent.agent_id ? 'opacity-100' : 'opacity-0'}`} />
                              {(agent.name || 'Unnamed Agent')} ({agent.agent_id})
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={loadData}>Apply Filters</Button>
              <Button
                variant="outline"
                onClick={() => {
                  setPeriodType('30');
                  setBusinessFilter('');
                  setAgentFilter('');
                  setStartDate('');
                  setEndDate('');
                }}
              >
                Clear
              </Button>
            </div>
            {data?.period_label && (
              <p className="text-sm text-slate-500">
                Active period: {data.period_label}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <PhoneCall className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Calls</p>
                <p className="text-2xl font-jakarta font-bold text-slate-900">
                  {data?.total_calls || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Minutes</p>
                <p className="text-2xl font-jakarta font-bold text-slate-900">
                  {data?.total_minutes || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg per Day</p>
                <p className="text-2xl font-jakarta font-bold text-slate-900">
                  {Math.round((data?.total_calls || 0) / Math.max(data?.period_days || 1, 1))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle>Calls Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
                    <YAxis stroke="#64748B" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="calls" 
                      stroke="#0066FF" 
                      strokeWidth={2}
                      dot={{ fill: '#0066FF', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  No call data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle>Minutes Used</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
                    <YAxis stroke="#64748B" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }} 
                    />
                    <Bar dataKey="minutes" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  No minute data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function AdminRevenuePage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState('30');

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await adminGetRevenueAnalytics(parseInt(days));
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <AnalyticsSkeleton />;
  }

  const chartData = data?.daily_revenue?.map(d => ({
    date: d._id,
    revenue: d.revenue,
    transactions: d.transactions,
  })) || [];

  const pieData = data?.revenue_by_type?.map(d => ({
    name: d._id === 'subscription' ? 'Subscriptions' : 'Packages',
    value: d.revenue,
  })) || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-revenue-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-jakarta font-bold text-slate-900">Revenue Reports</h1>
          <p className="text-slate-500">Track earnings and billing metrics</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-36" data-testid="revenue-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="border-0 shadow-md bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-green-700">Total Revenue</p>
                <p className="text-3xl font-jakarta font-bold text-green-900">
                  {formatCurrency(data?.total_revenue || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {data?.revenue_by_type?.map((type, i) => (
          <Card key={type._id} className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  type._id === 'subscription' ? 'bg-blue-50' : 'bg-purple-50'
                }`}>
                  {type._id === 'subscription' ? (
                    <CreditCard className="w-6 h-6 text-blue-600" />
                  ) : (
                    <Package className="w-6 h-6 text-purple-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-500 capitalize">{type._id}s</p>
                  <p className="text-2xl font-jakarta font-bold text-slate-900">
                    {formatCurrency(type.revenue)}
                  </p>
                  <p className="text-xs text-slate-500">{type.count} transactions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle>Revenue Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
                    <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${v}`} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#10B981" 
                      strokeWidth={2}
                      dot={{ fill: '#10B981', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  No revenue data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle>Revenue by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  No revenue breakdown available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AnalyticsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <Skeleton className="h-10 w-36" />
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="border-0 shadow-md">
            <CardContent className="p-6">
              <Skeleton className="w-12 h-12 rounded-xl mb-4" />
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <Skeleton className="h-[300px] w-full" />
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <Skeleton className="h-[300px] w-full" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
